<template>
	<header class="activity-detail-header" :class="{ inline }">
		<span
			v-if="label"
			class="type-label"
			:class="{
				comment: notificationTopic === NotificationTopic.CommentedOpportunity,
				'new-opportunity': notificationTopic === NotificationTopic.SavedSearchNewOpportunity,
				like: notificationTopic === NotificationTopic.LikedOpportunity,
				dislike: notificationTopic === NotificationTopic.DislikedOpportunity,
				'new-target': notificationTopic === NotificationTopic.TargetCreated,
			}"
		>
			<span class="type-icon" aria-hidden="true"></span>
			{{ label }}
		</span>
		<h2>{{ title }}</h2>
		<time :datetime="timestamp.toISOString()">
			<span class="clock-icon" aria-hidden="true"></span>
			{{ formattedTimestamp }}
		</time>
	</header>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useNow } from "@vueuse/core"
import { useI18n } from "vue-i18n"
import { NotificationTopic } from "@/graphql"
import { formatRelativeDateTime } from "@/utils/date"

const props = withDefaults(
	defineProps<{
		title: string
		timestamp: Date
		notificationTopic: NotificationTopic
		inline?: boolean
	}>(),
	{
		inline: false,
	}
)

const { locale, t } = useI18n()
const now = useNow({ interval: 60_000 })

const label = computed(() => {
	switch (props.notificationTopic) {
		case NotificationTopic.CommentedOpportunity:
			return t("activityPage.details.comment")
		case NotificationTopic.SavedSearchNewOpportunity:
			return t("activityPage.details.newOpportunity")
		case NotificationTopic.LikedOpportunity:
			return t("activityPage.details.likedTarget")
		case NotificationTopic.DislikedOpportunity:
			return t("activityPage.details.dislikedTarget")
		case NotificationTopic.TargetCreated:
			return t("activityPage.details.targetCreated")
		default:
			return null
	}
})

const formattedTimestamp = computed(() =>
	formatRelativeDateTime(props.timestamp, now.value, locale.value, (key, values) => t(key, values))
)
</script>

<style scoped>
.activity-detail-header {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: var(--spacing-sm);

	.type-label {
		display: inline-flex;
		align-items: center;
		gap: var(--spacing-xs);
		padding: var(--spacing-xs) var(--space-12);
		border-radius: var(--border-radius-full);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-semibold);

		&.comment {
			background: var(--accent-info-background);
			color: var(--accent-file);

			.type-icon {
				mask-image: url("/icons/activity-comment.svg");
			}
		}

		&.new-opportunity {
			background: color-mix(in srgb, var(--primary-green) 8%, var(--neutral-0));
			color: var(--primary-green);

			.type-icon {
				mask-image: url("/icons/activity-new-listing.svg");
			}
		}

		&.like {
			background: color-mix(in srgb, var(--accent-dev-potential-medium) 12%, var(--neutral-0));
			color: var(--accent-dev-potential-medium);

			.type-icon {
				mask-image: url("/icons/activity-like.svg");
			}
		}

		&.dislike {
			background: var(--neutral-100);
			color: var(--neutral-600);

			.type-icon {
				mask-image: url("/icons/activity-dislike.svg");
			}
		}

		&.new-target {
			background: color-mix(in srgb, var(--accent-target) 10%, var(--neutral-0));
			color: var(--accent-target);

			.type-icon {
				mask-image: url("/icons/activity-new-target.svg");
			}
		}
	}

	.type-icon {
		display: inline-block;
		width: var(--size-icon-md);
		height: var(--size-icon-md);
		background: currentColor;
		mask-position: center;
		mask-repeat: no-repeat;
		mask-size: contain;
	}

	h2 {
		margin: 0;
		color: var(--neutral-900);
		font-size: var(--font-size-medium);
		font-weight: var(--font-weight-semibold);
		line-height: var(--line-height-tight);
	}

	time {
		display: inline-flex;
		align-items: center;
		gap: var(--spacing-xs);
		color: var(--neutral-400);
		font-size: var(--font-size-xs);

		.clock-icon {
			width: var(--space-12);
			height: var(--space-12);
			background: currentColor;
			mask: url("/icons/activity-clock.svg") center / contain no-repeat;
		}
	}

	&.inline {
		display: contents;

		.type-label {
			grid-area: badge;
			justify-self: end;
			align-self: start;
		}

		h2 {
			grid-area: title;
			align-self: center;
			padding-right: var(--spacing-sm);
		}

		time {
			grid-area: time;
		}
	}
}
</style>
